import PropTypes from 'prop-types'
import { gql } from '@apollo/client'
import {
  sharedEventDataFragment,
  sharedEventDataFragmentPropTypes,
} from '~/graphql/fragments/sharedEventData'
import externalPrimaryTicketShopFragment, {
  externalPrimaryTicketShopFragmentPropTypes,
} from './externalPrimaryTicketShop'

const eventTypeDataFragment = gql`
  fragment eventTypeData on EventType {
    id
    slug
    title
    startDate
    endDate
    availableTicketsCount
  }
`

const eventTypeDataFragmentPropTypes = PropTypes.shape({
  id: PropTypes.string.isRequired,
  slug: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  startDate: PropTypes.string.isRequired,
  endDate: PropTypes.string,
  availableTicketsCount: PropTypes.number.isRequired,
})

const eventFragment = gql`
  fragment event on Event {
    ...sharedEventData
    availableTicketsCount
    soldTicketsCount
    ticketAlertsCount
    cancellationReason
    entranceTypes: types(first: 99, filter: { ticketCategory: ENTRANCE }) {
      edges {
        node {
          ...eventTypeData
        }
      }
    }
    nonEntranceTypesWithoutGroup: types(
      first: 99
      filter: { ticketCategory: NON_ENTRANCE, hasGroup: false }
    ) {
      edges {
        node {
          ...eventTypeData
        }
      }
    }
    eventTypeGroups {
      id
      name
      eventTypes {
        ...eventTypeData
      }
    }
    redirectedTo {
      id
      slug
    }
    availableListings: listings(first: 2, filter: { listingStatus: AVAILABLE }) {
      edges {
        node {
          id
          seller {
            id
            avatar
          }
        }
      }
    }
    soldListings: listings(first: 2, filter: { listingStatus: SOLD }) {
      edges {
        node {
          id
          seller {
            id
            avatar
          }
        }
      }
    }
    usersWithTicketAlerts(first: 8) {
      edges {
        node {
          id
          avatar
        }
      }
    }
    externalPrimaryTicketShops {
      ...externalPrimaryTicketShop
    }
  }

  ${sharedEventDataFragment}
  ${eventTypeDataFragment}
  ${externalPrimaryTicketShopFragment}
`

export default eventFragment

export const eventFragmentPropTypes = PropTypes.shape({
  ...sharedEventDataFragmentPropTypes,
  availableTicketsCount: PropTypes.number,
  soldTicketsCount: PropTypes.number,
  ticketAlertsCount: PropTypes.number,
  externalPrimaryTicketShop: PropTypes.arrayOf(externalPrimaryTicketShopFragmentPropTypes),
  entranceTypes: PropTypes.shape({
    edges: PropTypes.arrayOf(
      PropTypes.shape({
        node: PropTypes.shape({
          eventTypeDataFragmentPropTypes,
        }),
      })
    ),
  }),
  nonEntranceTypesWithoutGroup: PropTypes.shape({
    edges: PropTypes.arrayOf(
      PropTypes.shape({
        node: PropTypes.shape({
          eventTypeDataFragmentPropTypes,
        }),
      })
    ),
  }),
  eventTypeGroups: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      name: PropTypes.string,
      eventTypes: PropTypes.arrayOf(eventTypeDataFragmentPropTypes),
    })
  ),
  redirectedTo: PropTypes.shape({
    id: PropTypes.string.isRequired,
    slug: PropTypes.string.isRequired,
  }),
  availableListings: PropTypes.shape({
    edges: PropTypes.arrayOf(
      PropTypes.shape({
        node: PropTypes.shape({
          id: PropTypes.string.isRequired,
          seller: PropTypes.shape({
            id: PropTypes.string.isRequired,
            avatar: PropTypes.string,
          }),
        }),
      })
    ),
  }),
  soldListings: PropTypes.shape({
    edges: PropTypes.arrayOf(
      PropTypes.shape({
        node: PropTypes.shape({
          id: PropTypes.string.isRequired,
          seller: PropTypes.shape({
            id: PropTypes.string.isRequired,
            avatar: PropTypes.string,
          }),
        }),
      })
    ),
  }),
  usersWithTicketAlerts: PropTypes.shape({
    edges: PropTypes.arrayOf(
      PropTypes.shape({
        node: PropTypes.shape({
          id: PropTypes.string.isRequired,
          avatar: PropTypes.string,
        }),
      })
    ),
  }),
})
